import { useToast } from "@chakra-ui/react";
import axios from 'axios';
import { usePersistedState } from "hooks/usePersistedState";
import { LoginCredentials, UserData, UserInformation } from "models/auth.model";
import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { wait } from "utils/wait";
import { AuthAPI } from "_config/auth.api";

type AuthContextData = {
    signedIn: boolean;
    user: UserData | null;
    info: UserInformation | null;
    refreshUserInformation: () => Promise<void>;
    signIn(data: LoginCredentials): Promise<void>;
    signOut(): Promise<void>;
};

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthProvider({ children }: PropsWithChildren<{}>) {
    const [userData, setUserData] = usePersistedState<UserData | null>('@EGL/user-token', null);
    const [userInfo, setUserInfo] = useState<UserInformation | null>(null);

    const toast = useToast();
    const navigate = useNavigate();

    async function signIn(data: LoginCredentials) {
        const response = await AuthAPI.login(data.cpf, data.password);
        await setUserData(response);
        await wait(500);
    }

    async function signOut() {
        await setUserData(null);
        setUserInfo(null);
        navigate('/login');
    }

    async function refreshUserInformation() {
        setUserInfo(null);
        try {
            const userInfo = await AuthAPI.getProfileData();
            setUserInfo(userInfo);
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status === 401) {
                await refreshToken();
            }
        }
    }

    async function refreshToken() {
        try {
            const data = await AuthAPI.refreshToken();
            setUserData(data);
        } catch (err: any) {
            if (axios.isAxiosError(err) && err.response?.status === 401) {
                toast({
                    title: 'Sessão expirada',
                    description: 'Sua sessão expirou! Tente entrar novamente com suas credenciais.',
                    status: 'info'
                });
                await signOut();
            }
        }
    }

    useEffect(() => {
        refreshUserInformation();
    }, [userData]);


    useEffect(() => {
        refreshToken();
    }, []);


    return (
        <AuthContext.Provider
            value={{
                signedIn: !!userData,
                user: userData,
                info: userInfo ?? null,
                refreshUserInformation,
                signIn,
                signOut
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);

    return context;
}